<template>
    <Card>
        <FetchTable
            :handle="tableHandle"
            :url="`/projects/${$route.params.projectUuid}/wms/transfers/${$route.params.transferUuid}/products`"
            :columns="columns"
            :has-checkbox="true"
            @update:selectedValues="selectedProductUuids = $event"
            has-filters
            :active-filters-count="activeFiltersCount"
            @click:filters-button="$refs.ProductsFiltersModal.show()"
            @click:clear-filters="clearFilters"
            :disableCheckbox="transfer?.completed_at !== null"
        >
            <template #actionDropdownItems>
                <DropdownItem @click="$refs.DeleteProductsModal.show(selectedProductUuids)">{{ $t('buttons.delete') }}</DropdownItem>
            </template>
            <template #table-body>
                <tr>
                    <td></td>
                    <td>
                        <AppButton color="light" size="sm" class="btn-icon" v-if="newProduct" @click="clearNewTransferProduct">
                            <i class="icon-cross2"></i>
                        </AppButton>
                    </td>
                    <td>
                        <AlphaButton color="teal" size="sm" v-if="newProduct" @click="addNewTransferProduct">
                            <i class="icon-plus3"></i>
                        </AlphaButton>
                    </td>
                    <td>
                        <AppAutoComplete
                            url="/catalog/product-items"
                            :model-value="newProduct?.uuid"
                            :selectedValue="newProduct?.name"
                            @update:model-value="selectNewTransferProduct"
                            :disabled="transfer?.completed_at !== null"
                        >
                            <template #optionItem="{ option }">
                                <div class="d-flex align-items-center gap-2">
                                    <img :src="option.main_image_url" :style="{ width: '38px', minWidth: '38px', height: '38px', objectFit: 'cover' }" class="mr-2" />
                                    <div>
                                        <span class="long-string" :title="option.name">{{ option.name }}</span>
                                        <small class="long-string text-muted" :title="option.sku">{{ option.sku }}</small>
                                    </div>
                                </div>
                            </template>
                        </AppAutoComplete>
                    </td>
                    <td>
                        <div v-if="newProduct">
                            <AppTouchSpinInput
                                v-model="newProductAmount"
                                :min="1"
                            />
                        </div>
                    </td>
                    <td class="text-right">
                        <span  v-if="newProduct">
                            {{ newProductBalance }}
                        </span>
                    </td>
                    <td class="text-right">
                        <span v-if="newProduct" :class="{
                            'text-danger': newProductAmount > newProductBalance
                        }">
                            {{ newProductBalance - newProductAmount }}
                        </span>
                    </td>
                </tr>
            </template>
            <template #td-action="{ row }">
                <AlphaButton color="danger" size="sm" @click="deleteTransferProduct(row)" :disabled="transfer?.completed_at !== null">
                    <i class="icon-trash"></i>
                </AlphaButton>
            </template>
            <template #td-image="{ row }">
                <ImageZoomComponent
                    :url="row.product.main_image_url !== null ? row.product.main_image_url : PLACEHOLDER_IMAGE_URL"
                    :alt="row.product.name"
                    :default-width="50"
                    :default-height="50"
                />
            </template>
            <template #td-name="{ row }">
                <div>
                    <span class="long-string">
                        {{ row.product.name }}
                    </span>
                    <span class="text-muted">{{ row.product.sku }}</span>
                </div>
            </template>
            <template #td-amount="{ row }">
                <AppTouchSpinInput
                    :model-value="row.amount"
                    @update:modelValue="(newAmount) => updateAmount(row, newAmount)"
                    :min="1"
                    :disabled="transfer?.completed_at !== null"
                />
            </template>
            <template #td-diff_amount="{ row }">
                <span :class="{
                    'text-danger': row.diff_amount < 0
                }">
                    {{ row.diff_amount }}
                </span>
            </template>
        </FetchTable>
    </Card>
    <DeleteProductsModal ref="DeleteProductsModal" @products:deleted="fetchProducts"/>
    <ProductsFiltersModal ref="ProductsFiltersModal" @update:filters="fetchProducts" />
</template>
<script>
import Card from '../../../../../components/new/CardComponent.vue';
import FetchTable from '../../../../../components/new/table/FetchTable.vue';
import { createTableHandle } from '../../../../../components/new/table/tableHandle';
import AppButton from '../../../../../components/new/button/AppButton.vue';
import AlphaButton from '../../../../../components/new/button/AlphaButton.vue';
import AppAutoComplete from '../../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import AppTouchSpinInput from '../../../../../components/new/form/AppTouchSpinInput.vue';
import DeleteProductsModal from '../modals/DeleteProductsModal.vue';
import DropdownItem from "../../../../components/btnDropdown/DropdownItem.vue";
import ProductsFiltersModal from '../modals/ProductsFiltersModal.vue';
import ImageZoomComponent from '../../../../../components/new/image/ImageZoomComponent.vue';

import { httpClient } from '../../../../../services/HttpClient';
import MessageNotificator from '../../../../../services/MessageNotificator';
import { PLACEHOLDER_IMAGE_URL } from '../../../../../constants.ts';

export default {
    name: 'ProductsCard',
    components: {
        Card, FetchTable, AppButton, AlphaButton, AppAutoComplete, AppTouchSpinInput, DeleteProductsModal, DropdownItem, ProductsFiltersModal, ImageZoomComponent
    },
    props: {
        transfer: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            tableHandle: createTableHandle(),
            PLACEHOLDER_IMAGE_URL,
            columns: [
                { key: "action", name: this.$t('table.titles.action'), width: "34px", type: "string", order: false },
                { key: "image", name: this.$t('table.titles.image'), width: "80px", type: "string", order: false },
                { key: "name", name: this.$t('table.titles.name'), width: "300px", type: "string", order: false },
                { key: "amount", name: this.$t('table.titles.amount'), width: "150px", type: "string", order: false },
                { key: "exists_amount", name: this.$t('table.titles.existing_amount'), width: "150px", type: "number", order: false },
                { key: "diff_amount", name: this.$t('table.titles.difference_amount'), width: "150px", type: "number", order: false }
            ],
            newProduct: null,
            newProductAmount: 1,
            newProductBalance: 0,
            selectedProductUuids: [],
            totalProducts: 0,
            activeFiltersCount: 0
        }
    },
    methods: {
        clearFilters() {
            this.$refs.ProductsFiltersModal.clear();
        },
        selectNewTransferProduct(product) {
            this.newProduct = product;
            this.fetchAvailability(product.uuid);

            if (product) {
                this.newProductAmount = 1;
            } else {
                this.clearNewTransferProduct();
            }
        },
        clearNewTransferProduct() {
            this.newProduct = null;
            this.newProductAmount = 1;
        },
        filterNegativeDiff() {
            if (this.$refs.ProductsFiltersModal) {
                const field = this.$refs.ProductsFiltersModal.fields.find(field => field.key === 'diff_amount_is_negative');
                if (field) {
                    field.value = 1;
                    field.selectedAction = { key: 'no', label: this.$t('labels.no') };
                    this.fetchProducts();
                }
            }
        },
        async fetchProducts(manualFilters = {}) {
            let filters = {};

            if (this.$refs.ProductsFiltersModal) {
                this.activeFiltersCount = this.$refs.ProductsFiltersModal.fields.filter(f => f.selectedAction !== null).length;
                this.$refs.ProductsFiltersModal.fields.forEach(field => {
                    if (field.value !== null) {
                        filters[field.key] = field.value;
                    }
                });
            }

            filters = { ...filters, ...manualFilters };

            await this.tableHandle.fetchRows(filters);
            this.totalProducts = this.tableHandle.total;
        },
        async addNewTransferProduct() {
            if (!this.newProduct) return;

            await httpClient.post(`/projects/${this.$route.params.projectUuid}/wms/transfers/${this.$route.params.transferUuid}/products`, {
                product_uuid: this.newProduct.uuid,
                amount: this.newProductAmount
            }).then(() => {
                this.fetchProducts();
                this.clearNewTransferProduct();
            })
        },
        async updateAmount(transferProduct, newAmount) {
            await httpClient.put(`/projects/${this.$route.params.projectUuid}/wms/transfers/${this.$route.params.transferUuid}/products/${transferProduct.uuid}`, {
                amount: newAmount
            }).then(() => {
                this.fetchProducts();
            })
        },
        async deleteTransferProduct(transferProduct) {
            if (this.transfer?.completed_at !== null) return;
            new MessageNotificator().confirm(this.$t('alerts.confirm_delete', {
                value: this.$t('labels.product')
            }), async () => {
                await httpClient.delete(`/projects/${this.$route.params.projectUuid}/wms/transfers/${this.$route.params.transferUuid}/products/${transferProduct.uuid}`).then(() => {
                    this.fetchProducts();
                })
            })
        },
        fetchAvailability(productUuid) {
            httpClient.get('/management/warehouses/products/availability', {
                product_uuids: [productUuid]
            }).then(response => {
                let fromWarehouseBalance = response.data.data.find(item => item.warehouse_uuid === this.transfer.warehouse_from.uuid)?.balance || 0;

                this.newProductBalance = fromWarehouseBalance;
            })
        }
    },
    mounted() {
        this.fetchProducts();
    }
}
</script>
<style scoped>
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
}
</style>